export interface BattleParticipationOrderLike {
  id?: string | number;
  createdAt?: string | number | Date;
  side?: string | null;
  character?: {
    id?: string | number;
    name?: string;
  };
}

const SIDE_ORDER: Record<string, number> = {
  left: 0,
  a: 0,
  team1: 0,
  team_1: 0,
  right: 1,
  b: 1,
  team2: 1,
  team_2: 1,
};

const getExplicitOrder = (participation: BattleParticipationOrderLike): number | null => {
  if (typeof participation.side === 'string') {
    return SIDE_ORDER[participation.side.toLowerCase()] ?? null;
  }

  return null;
};

const getStableKey = (participation: BattleParticipationOrderLike): string => {
  return String(
    participation.id ??
      participation.character?.id ??
      participation.character?.name ??
      ''
  );
};

const getCreatedAtTime = (participation: BattleParticipationOrderLike): number | null => {
  if (!participation.createdAt) return null;

  const time = new Date(participation.createdAt).getTime();
  return Number.isFinite(time) ? time : null;
};

export const sortBattleParticipations = <T extends BattleParticipationOrderLike>(
  participations: T[] | null | undefined
): T[] => {
  return [...(participations ?? [])].sort((a, b) => {
    const explicitOrderA = getExplicitOrder(a);
    const explicitOrderB = getExplicitOrder(b);

    if (explicitOrderA != null || explicitOrderB != null) {
      return (explicitOrderA ?? Number.MAX_SAFE_INTEGER) - (explicitOrderB ?? Number.MAX_SAFE_INTEGER);
    }

    const createdAtA = getCreatedAtTime(a);
    const createdAtB = getCreatedAtTime(b);

    if (createdAtA != null || createdAtB != null) {
      const createdAtCompare = (createdAtA ?? Number.MAX_SAFE_INTEGER) - (createdAtB ?? Number.MAX_SAFE_INTEGER);
      if (createdAtCompare !== 0) return createdAtCompare;
    }

    return getStableKey(a).localeCompare(getStableKey(b));
  });
};

export const withStableBattleParticipations = <
  T extends { participations?: P[] | null },
  P extends BattleParticipationOrderLike,
>(battle: T): T => ({
  ...battle,
  participations: sortBattleParticipations(battle.participations) as T['participations'],
});
